import type {
    CaseTypeDetail,
    GetCaseSummaryResponseItem,
    Retailer,
    RetailerProfile,
    VinDetail,
} from '@packages/clients';

import { toBoolean } from '@packages/core/utils';

import type { DtcCodeEntity } from '../../../dtc-codes';
import type { RetailerProfileEntity } from '../../../retailers/entities';
import type { VinDetailEntity } from '../../../vin/entities';
import type {
    WorksheetSubmissionEntity,
    WorksheetSubmissionGroupEntity,
    WorksheetSubmissionTitleEntity,
} from '../../worksheet-submissions/entities';
import type { CaseTypeEntity, TechlineCaseEntity, TechlineCaseSummary } from '../entities';

import {
    isCidCvnCase,
    isClosedCase,
    isDraftCase,
    isEscalatedCase,
    isSubmittedCase,
    resolveAllowedCaseFeatures,
} from './checks';
import { normalizeTechlineStatus } from './transformers';

/** A worksheet case type DTO from the case types endpoint. */
type CaseTypeDto = {
    /** The case type name (e.g. "Body", "Powertrain"). */
    caseType: string;

    /** The internal case type identifier. */
    caseTypeId: string;

    /** The NorthStar identifier. */
    northstarId: string;
};

/**
 * Map a `CaseTypeDetail` or `CaseType` DTO to a `CaseTypeEntity`. Detects the shape
 * by checking for the `caseTypeName` property (present only on `CaseTypeDetail`).
 *
 * @param dto - the case type DTO from either API shape
 * @returns the mapped entity
 * @example
 * ```ts
 * const entity = toCaseTypeEntity({ caseTypeId: 'ct1', northstarId: 'ns1', caseType: 'Body' });
 * ```
 */
export function toCaseTypeEntity(dto: CaseTypeDetail | CaseTypeDto): CaseTypeEntity {
    if ('caseTypeName' in dto) {
        return toCaseTypeEntityFromDetail(dto);
    }
    return toCaseTypeEntityFromCaseType(dto);
}

/**
 * Map a `CaseType` DTO (from the worksheet case types endpoint) to a `CaseTypeEntity`.
 * Copies all fields from the DTO into the entity shape.
 *
 * @param dto - the case type DTO from the worksheets API
 * @returns the mapped entity
 * @example
 * ```ts
 * const entity = toCaseTypeEntityFromCaseType({ caseTypeId: 'ct1', northstarId: 'ns1', caseType: 'Powertrain' });
 * ```
 */
export function toCaseTypeEntityFromCaseType(dto: CaseTypeDto): CaseTypeEntity {
    return {
        caseType: dto.caseType,
        caseTypeId: dto.caseTypeId,
        northstarId: dto.northstarId,
    };
}

/**
 * Map a `CaseTypeDetail` DTO (from the case details response) to a `CaseTypeEntity`.
 * Copies all fields from the DTO into the entity shape.
 *
 * @param dto - the case type detail DTO from the API
 * @returns the mapped entity
 * @example
 * ```ts
 * const entity = toCaseTypeEntityFromDetail({ caseTypeSourceId: 'src1', caseTypeId: 'ct1', caseTypeName: 'Body', stateCode: '0', statusCode: '1' });
 * ```
 */
export function toCaseTypeEntityFromDetail(dto: CaseTypeDetail): CaseTypeEntity {
    return {
        caseTypeDescription: dto.caseTypeDescription,
        caseTypeId: dto.caseTypeId,
        caseTypeName: dto.caseTypeName,
        caseTypeSourceId: dto.caseTypeSourceId,
        stateCode: dto.stateCode,
        statusCode: dto.statusCode,
    };
}

// Exception: 1:1 field copy. This mapper exists as an intentional DTO-to-entity boundary firewall so that
// domain code never depends directly on the RetailerProfile DTO type, even though the shapes currently match.
/**
 * Map a `RetailerProfile` DTO to a `RetailerProfileEntity`. The shapes are currently
 * identical, so this is a straightforward copy. Having the mapper in place ensures
 * domain code never depends directly on the DTO type.
 *
 * @param dto - the retailer profile DTO from the API
 * @returns the mapped entity
 * @example
 * ```ts
 * const entity = toRetailerProfileEntity({ retailerNumber: '123', name: 'Acme Subaru', isPrimary: true, ... });
 * ```
 */
export function toRetailerProfileEntity(dto: RetailerProfile): RetailerProfileEntity {
    return {
        city: dto.city,
        createdOn: dto.createdOn,
        districtCode: dto.districtCode,
        districtId: dto.districtId,
        isPrimary: dto.isPrimary,
        name: dto.name,
        postalCode: dto.postalCode,
        regionCode: dto.regionCode,
        regionId: dto.regionId,
        retailerNumber: dto.retailerNumber,
        retailerProfileId: dto.retailerProfileId,
        soaUserId: dto.soaUserId,
        state: dto.state,
        userName: dto.userName,
        zoneCode: dto.zoneCode,
        zoneId: dto.zoneId,
    };
}

/**
 * Map a `Retailer` DTO to a `TechlineCaseEntity`. Converts nested retailer
 * profile arrays to entity types and copies all other fields directly. The
 * shapes are structurally equivalent; this mapper exists to establish the
 * boundary between DTO and domain layers.
 *
 * @param dto - the case details DTO from the API (typed as `Retailer`)
 * @returns the mapped entity
 * @example
 * ```ts
 * const entity = toTechlineCaseEntity(apiResponse.data.caseDetails);
 * ```
 */
export function toTechlineCaseEntity(dto: Retailer): TechlineCaseEntity {
    // Normalize the DTO status to an official domain status before deriving booleans
    const techlineStatusCode = normalizeTechlineStatus(dto.techlineStatusCode);

    return {
        allowCaseFeatures: resolveAllowedCaseFeatures(dto),
        asset: dto.asset,
        caseId: dto.caseId,
        caseTypes: dto.caseTypes,
        diagnosticTroubleCodes: dto.diagnosticTroubleCodes.map(toDtcCodeEntity),
        failCode: dto.failCode,
        isCidCvnCase: isCidCvnCase(dto.primaryCaseType, dto.caseTypes),
        isClosed: isClosedCase(techlineStatusCode),
        isDraft: isDraftCase(techlineStatusCode),
        isEscalated: isEscalatedCase(techlineStatusCode),
        isSubmitted: isSubmittedCase(techlineStatusCode),
        mileage: dto.mileage,
        modifiedOn: dto.modifiedOn,
        modifiedOnDescription: dto.modifiedOnDescription,
        previousMileage: dto.previousMileage,
        previousRONumber: dto.previousRONumber,
        primaryCaseType: dto.primaryCaseType,
        product: dto.product,
        repairCode: dto.repairCode,
        repairOrderNumber: dto.repairOrderNumber,
        repairOrderOpenDate: dto.repairOrderOpenDate,
        retailerProfiles: dto.retailerProfiles.map(toRetailerProfileEntity),
        retailerUniqueProfiles: dto.retailerUniqueProfiles?.map(toRetailerProfileEntity),
        section: dto.section,
        stateCode: dto.stateCode,
        statusCode: dto.statusCode,
        subSection: dto.subSection,
        techShareCaseClosureDescription: dto.techShareCaseClosureDescription,
        techlineStatusCode,
        WorksheetSubmissionGroups: dto.WorksheetSubmissionGroups?.map(toWorksheetSubmissionGroupEntity),
        worksheetSubmissions: dto.worksheetSubmissions.map(toWorksheetSubmissionEntity),
        ...mapConcernAndSafetyFields(dto),
    };
}

/**
 * Map a `GetCaseSummaryResponseItem` DTO (from the cases list endpoint) to a `TechlineCaseSummary`.
 *
 * @param dto - the case summary DTO from the API
 * @returns  the mapped entity
 * @example
 * ```ts
 * const entity = toTechlineCaseSummary(apiResponse.data.results[0]);
 * ```
 */
export function toTechlineCaseSummary(dto: GetCaseSummaryResponseItem): TechlineCaseSummary {
    return {
        additionalProperties: dto.additionalProperties,
        carline: dto.carline,
        caseNumber: dto.caseNumber,
        caseType: dto.caseType,
        caseTypeId: dto.caseTypeId,
        createdOn: dto.createdOn,
        customerConcern: dto.customerConcern,
        customerId: dto.customerId,
        fullName: dto.fullName,
        modifiedOn: dto.modifiedOn,
        openAssignmentsCount: dto.openAssignmentsCount,
        repairCode: dto.repairCode,
        repairOrderNumber: dto.repairOrderNumber,
        techlineCaseStatus: dto.techlineCaseStatus,
        userName: dto.userName,
        vin: dto.vin,
        worksheets: dto.worksheets,
    };
}

// Exception: 1:1 field copy. This mapper exists as an intentional DTO-to-entity boundary firewall so that
// domain code never depends directly on the VinDetail DTO type, even though the shapes currently match.
/**
 * Map a `VinDetail` DTO to a `VinDetailEntity`. The shapes are currently
 * identical; this mapper establishes the DTO-to-domain boundary.
 *
 * @param dto - the VIN detail DTO from the API
 * @returns the mapped entity
 * @example
 * ```ts
 * const entity = toVinDetailEntity(apiResponse.data.vinDetails[0]);
 * ```
 */
export function toVinDetailEntity(dto: VinDetail): VinDetailEntity {
    return {
        colorCategory: dto.colorCategory,
        colorDescription: dto.colorDescription,
        emissionSpec: dto.emissionSpec,
        engineId: dto.engineId,
        engineSize: dto.engineSize,
        model: dto.model,
        modelNumber: dto.modelNumber,
        modelYear: dto.modelYear,
        optionCode: dto.optionCode,
        productionDate: dto.productionDate,
        transmissionType: dto.transmissionType,
        trimDescription: dto.trimDescription,
        turboIndicator: dto.turboIndicator,
        vehicleTransmissionNumber: dto.vehicleTransmissionNumber,
        vin: dto.vin,
        vinStatus: dto.vinStatus,
        warrantyStartDt: dto.warrantyStartDt,
    };
}

// ──────────────────────────────────────────────
// Internal mapper helpers (not exported)
// ──────────────────────────────────────────────

/**
 * Extract the concern-detail and safety-flag fields from a case DTO into a
 * partial `TechlineCaseEntity`. These fields describe the technician's concern
 * narrative and the incident/safety indicators; they are separated from the
 * main mapper to keep `toTechlineCaseEntity` under the line-count limit.
 *
 * @param dto - the case details DTO
 * @returns a partial entity containing concern and safety fields
 */
function mapConcernAndSafetyFields(
    dto: Retailer
): Pick<
    TechlineCaseEntity,
    | 'accidentFlag'
    | 'airbagRuptureFlag'
    | 'customerConcern'
    | 'deathFlag'
    | 'didTheConditionImprove'
    | 'fireFlag'
    | 'hasBeenAbleToDuplicate'
    | 'injuryFlag'
    | 'previousDiagnosisAndRepairs'
    | 'propertyDamage'
    | 'rolloverFireAccidentInjuryDeathDamage'
    | 'rolloverFlag'
    | 'symptoms'
    | 'technician5StepsBeforeFailureDuplication'
    | 'technicianDescription'
    | 'vehicleHasBeenInBefore'
    | 'wasTheVehicleRepaired'
    | 'werePartsReplaced'
    | 'whatPartsWereReplaced'
> {
    return {
        accidentFlag: dto.accidentFlag,
        airbagRuptureFlag: dto.airbagRuptureFlag,
        customerConcern: dto.customerConcern,
        deathFlag: dto.deathFlag,
        didTheConditionImprove: dto.didTheConditionImprove,
        fireFlag: dto.fireFlag,
        hasBeenAbleToDuplicate: toBoolean(dto.hasBeenAbleToDuplicate),
        injuryFlag: dto.injuryFlag,
        previousDiagnosisAndRepairs: dto.previousDiagnosisAndRepairs,
        propertyDamage: dto.propertyDamage,
        rolloverFireAccidentInjuryDeathDamage: dto.rolloverFireAccidentInjuryDeathDamage,
        rolloverFlag: dto.rolloverFlag,
        symptoms: dto.symptoms,
        technician5StepsBeforeFailureDuplication: dto.technician5StepsBeforeFailureDuplication,
        technicianDescription: dto.technicianDescription,
        vehicleHasBeenInBefore: toBoolean(dto.vehicleHasBeenInBefore),
        wasTheVehicleRepaired: dto.wasTheVehicleRepaired,
        werePartsReplaced: toBoolean(dto.werePartsReplaced),
        whatPartsWereReplaced: dto.whatPartsWereReplaced,
    };
}

/**
 * Map a diagnostic trouble code DTO (embedded in case details) to a `DtcCodeEntity`.
 * Renames `diagnosticTroubleCode` to `dtcCode` and coerces `isPrimary` and
 * `freezeFrameDataInSSM` to booleans when present.
 *
 * @param dtc - the raw DTC object from the API response
 * @returns the mapped entity
 */
function toDtcCodeEntity(dtc: Retailer['diagnosticTroubleCodes'][number]): DtcCodeEntity {
    return {
        code: dtc.diagnosticTroubleCode,
        ...(dtc.freezeFrameDataInSSM != null && { hasFreezeFrameDataInSSM: toBoolean(dtc.freezeFrameDataInSSM) }),
        ...(dtc.isPrimary != null && { isPrimary: toBoolean(dtc.isPrimary) }),
    };
}

/**
 * Map a worksheet submission DTO (embedded in case details) to a
 * `WorksheetSubmissionEntity`.
 *
 * @param sub - the raw submission object from the API response
 * @returns the mapped entity
 */
function toWorksheetSubmissionEntity(sub: Retailer['worksheetSubmissions'][number]): WorksheetSubmissionEntity {
    return {
        lastUpdatedTimestamp: sub.lastUpdatedTimestamp,
        lastUpdatedTimestampForDisplay: sub.lastUpdatedTimestampForDisplay,
        retailerNumber: sub.retailerNumber,
        status: sub.status as string,
        statusCode: sub.statusCode,
        submissionId: sub.submissionId,
        userDisplayName: sub.userDisplayName,
        userName: sub.userName,
        worksheetCaseType: sub.worksheetCaseType,
        worksheetCaseTypeSourceId: sub.worksheetCaseTypeSourceId,
        worksheetId: sub.worksheetId,
        worksheetTitle: sub.worksheetTitle,
    };
}

/**
 * Map a worksheet submission group DTO (embedded in case details) to a
 * `WorksheetSubmissionGroupEntity`.
 *
 * @param group - the raw group object from the API response
 * @returns the mapped entity
 */
function toWorksheetSubmissionGroupEntity(
    group: NonNullable<Retailer['WorksheetSubmissionGroups']>[number]
): WorksheetSubmissionGroupEntity {
    return {
        groupDescription: group.groupDescription,
        groupName: group.groupName,
        worksheetCaseGroupId: group.worksheetCaseGroupId,
        worksheetSubmissionTitles: group.worksheetSubmissionTitles.map(toWorksheetSubmissionTitleEntity),
    };
}

/**
 * Map a worksheet submission title DTO (embedded in case details) to a
 * `WorksheetSubmissionTitleEntity`.
 *
 * @param title - the raw title object from the API response
 * @returns the mapped entity
 */
function toWorksheetSubmissionTitleEntity(
    title: NonNullable<Retailer['WorksheetSubmissionGroups']>[number]['worksheetSubmissionTitles'][number]
): WorksheetSubmissionTitleEntity {
    return {
        lastUpdatedTimestamp: title.lastUpdatedTimestamp,
        status: title.status,
        submissionId: title.submissionId,
        userDisplayName: title.userDisplayName,
        worksheetDescription: title.worksheetDescription,
        worksheetId: title.worksheetId,
        worksheetTitle: title.worksheetTitle,
    };
}
